import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Sparkles } from 'lucide-react';
import { useAuth, needsEmailConfirm, isViewingAs } from '../context/AuthContext';
import { CONSENT_SCOPES } from './ConsentFields';

/**
 * First-open consent sheet (GDPR Week 6 follow-up, 10 Jun 2026).
 *
 * Shows ONCE after login for any user who has consent scopes their age
 * permits but has never been asked about (answeredScopes from
 * GET /api/me/consents). Catches every account that predates consent
 * capture at registration. Never re-shows: declining writes 'withdrawn'
 * rows, so the scopes count as answered.
 *
 * Compliance notes (don't weaken these — invalid consent poisons the
 * audience-extension dataset):
 *  - "Yes, count me in" and "Just the essentials" are EQUAL prominence.
 *  - Nothing is pre-ticked; "Let me choose" starts all-off.
 *  - The server age-gates whatever we send (lib/consents.js).
 *  - source='first_open_prompt' is recorded on every row.
 *
 * Accounts missing a DOB (pre-dating age-gating) get a DOB step first so
 * the right scopes can be offered.
 */
export default function ConsentOnboardingSheet() {
  const { user, loading, impersonating } = useAuth();
  const location = useLocation();
  const qc = useQueryClient();
  const [choosing, setChoosing] = useState(false);
  const [picked, setPicked] = useState({});
  const [dob, setDob] = useState('');
  const [error, setError] = useState('');

  // Sponsor business accounts (and platform staff) are NOT the audience: the
  // analytics / audience-extension consent prompt is written for member
  // supporters and is meaningless — even misleading — to them ("helps your
  // club's sponsors reach more supporters like you" makes no sense to a
  // sponsor). There's no audience data to opt into, so we don't show it.
  // They still get all necessary processing. If a sponsor also joins a club
  // as a supporter, they stop being sponsor-only and the prompt returns.
  const memberships = user?.memberships || [];
  const isBusinessAccount =
    !!user?.isPlatformUser ||
    (memberships.length > 0 && memberships.every(m => m.role === 'sponsor'));

  // B-65: not on top of the Check your inbox screen; it shows once they're in.
  const enabled = !!user && !loading && !isViewingAs(impersonating) && !isBusinessAccount && !needsEmailConfirm(user, impersonating);
  // queryKey includes user.id: on account switch the new user must never be
  // served the previous user's cached consent state (login also clears the
  // whole cache — this is belt and braces).
  const { data } = useQuery({
    queryKey: ['me-consents', user?.id],
    queryFn: () => axios.get('/me/consents').then(r => r.data),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  const saveConsents = useMutation({
    mutationFn: (desired) =>
      axios.put('/me/consents', { ...desired, source: 'first_open_prompt' }).then(r => r.data),
    onSuccess: (fresh) => qc.setQueryData(['me-consents', user?.id], (old) => ({ ...old, ...fresh })),
    onError: (err) => setError(err.response?.data?.error || 'Could not save — please try again.'),
  });

  const saveDob = useMutation({
    mutationFn: () => axios.patch('/me/profile', { dateOfBirth: dob }).then(r => r.data),
    onSuccess: () => { setError(''); qc.invalidateQueries({ queryKey: ['me-consents'] }); },
    onError: (err) => setError(err.response?.data?.error || 'Could not save date of birth.'),
  });

  // Hide: not logged in, data not in yet, reading the policy, or nothing to ask.
  if (!enabled || !data || location.pathname === '/privacy') return null;
  const allowed = data.allowedScopes || [];
  const answered = data.answeredScopes || [];
  const unanswered = allowed.filter(s => !answered.includes(s));
  const needsDob = !data.hasDateOfBirth;
  if (!needsDob && unanswered.length === 0) return null;

  const allowedScopeDefs = CONSENT_SCOPES.filter(s => allowed.includes(s.key));

  const grantAll = () => {
    const all = {};
    for (const s of allowed) all[s] = true;
    saveConsents.mutate(all);
  };
  const declineAll = () => {
    const none = {};
    for (const s of allowed) none[s] = false;
    saveConsents.mutate(none);
  };
  const saveChosen = () => {
    const out = {};
    for (const s of allowed) out[s] = !!picked[s];
    saveConsents.mutate(out);
  };

  const btnBase = {
    width: '100%', justifyContent: 'center', padding: '12px 20px',
    fontSize: 15, borderRadius: 8,
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 1400 }}>
      <div className="card" style={{ maxWidth: 470, width: '100%', maxHeight: '88vh', overflowY: 'auto', padding: 26 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <span style={{
            width: 38, height: 38, borderRadius: 10, background: 'rgba(196,148,58,0.16)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Sparkles size={19} color="#C4943A" />
          </span>
          <h2 style={{ margin: 0, fontSize: 21 }}>
            {needsDob ? 'One quick thing' : `Welcome, ${user.firstName}`}
          </h2>
        </div>

        {needsDob ? (
          <>
            <p style={{ fontSize: 14.5, lineHeight: 1.55, color: 'var(--text-secondary)', margin: '8px 0 14px' }}>
              Tell us your date of birth so we can apply the right privacy protections
              for your age. Takes five seconds.
            </p>
            <input className="input" type="date" value={dob}
              max={new Date().toISOString().slice(0, 10)}
              onChange={e => setDob(e.target.value)} />
            <button className="btn btn-primary" style={{ ...btnBase, marginTop: 12 }}
              disabled={!dob || saveDob.isPending} onClick={() => saveDob.mutate()}>
              {saveDob.isPending ? 'Saving…' : 'Continue'}
            </button>
          </>
        ) : (
          <>
            <p style={{ fontSize: 14.5, lineHeight: 1.55, color: 'var(--text-secondary)', margin: '8px 0 4px' }}>
              Marakin is free for your club because local sponsors back it. Sharing how
              you use the app helps us make it better — and helps your club's sponsors
              reach more supporters like you, which keeps money flowing into grassroots sport.
            </p>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 16px' }}>
              Your choice, changeable any time in My account.{' '}
              <Link to="/privacy" target="_blank" style={{ color: 'var(--green)', textDecoration: 'underline' }}>
                Privacy policy
              </Link>
            </p>

            {choosing && (
              <div style={{ marginBottom: 14 }}>
                {allowedScopeDefs.map(s => (
                  <label key={s.key} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10, padding: '9px 10px',
                    borderRadius: 8, cursor: 'pointer',
                    background: picked[s.key] ? 'rgba(30,74,53,0.07)' : 'transparent',
                  }}>
                    <input type="checkbox" checked={!!picked[s.key]}
                      onChange={e => setPicked(p => ({ ...p, [s.key]: e.target.checked }))}
                      style={{ marginTop: 3, accentColor: 'var(--green, #1E4A35)' }} />
                    <span style={{ fontSize: 13.5, lineHeight: 1.35 }}>
                      <strong style={{ fontWeight: 700 }}>{s.label}</strong>
                      <span style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 12.5 }}>{s.detail}</span>
                    </span>
                  </label>
                ))}
              </div>
            )}

            {/* Equal-prominence accept / essentials-only — keep it that way. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {choosing ? (
                <button className="btn btn-primary" style={btnBase}
                  disabled={saveConsents.isPending} onClick={saveChosen}>
                  Save my choices
                </button>
              ) : (
                <button className="btn btn-primary" style={btnBase}
                  disabled={saveConsents.isPending} onClick={grantAll}>
                  Yes, count me in
                </button>
              )}
              <button className="btn btn-secondary" style={btnBase}
                disabled={saveConsents.isPending} onClick={declineAll}>
                Just the essentials
              </button>
              {!choosing && (
                <button className="btn btn-ghost" style={{ ...btnBase, padding: '8px 20px', fontSize: 13.5 }}
                  onClick={() => setChoosing(true)}>
                  Let me choose individually
                </button>
              )}
            </div>
          </>
        )}

        {error && <p style={{ marginTop: 10, fontSize: 13, color: 'var(--danger)' }}>{error}</p>}
      </div>
    </div>
  );
}
